import {
  getInquiries as dbGetInquiries,
  saveInquiry as dbSaveInquiry,
  updateInquiryStatus as dbUpdateInquiryStatus,
  deleteInquiry as dbDeleteInquiry,
  getInquiryById as dbGetInquiryById,
  initDB,
} from "./db";
import { isSupabaseConfigured, supabase } from "../lib/supabase";

const INQUIRIES_TABLE = "inquiries";

const normalizeInquiry = (row) => ({
  id: row.id,
  type: row.type,
  firstName: row.first_name ?? "",
  lastName: row.last_name ?? "",
  fullName:
    row.full_name ??
    [row.first_name, row.last_name].filter(Boolean).join(" ").trim(),
  email: row.email,
  phone: row.phone,
  subject: row.subject ?? "",
  product: row.product ?? "",
  message: row.message,
  status: row.status,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

const mapInquiryPayload = (inquiryData) => ({
  type: inquiryData.type,
  first_name: inquiryData.firstName ?? null,
  last_name: inquiryData.lastName ?? null,
  full_name: inquiryData.fullName ?? null,
  email: inquiryData.email,
  phone: inquiryData.phone ?? null,
  subject: inquiryData.subject ?? null,
  product: inquiryData.product ?? null,
  message: inquiryData.message,
  status: inquiryData.status ?? "new",
});

const withLocalFallback = async (operation) => {
  await initDB();
  return operation();
};

export const getInquiries = async () => {
  if (!isSupabaseConfigured) {
    return withLocalFallback(() => dbGetInquiries());
  }

  const { data, error } = await supabase
    .from(INQUIRIES_TABLE)
    .select("*")
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching inquiries from Supabase:", error);
    return [];
  }

  return data.map(normalizeInquiry);
};

export const saveInquiry = async (inquiryData) => {
  if (!isSupabaseConfigured) {
    return withLocalFallback(() => dbSaveInquiry(inquiryData));
  }

  const { data, error } = await supabase
    .from(INQUIRIES_TABLE)
    .insert(mapInquiryPayload(inquiryData))
    .select()
    .single();

  if (error) {
    console.error("Error saving inquiry to Supabase:", error);
    throw error;
  }

  return normalizeInquiry(data);
};

export const updateInquiryStatus = async (id, status) => {
  if (!isSupabaseConfigured) {
    return withLocalFallback(() => dbUpdateInquiryStatus(id, status));
  }

  const { data, error } = await supabase
    .from(INQUIRIES_TABLE)
    .update({
      status,
      updated_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("Error updating inquiry status in Supabase:", error);
    throw error;
  }

  return normalizeInquiry(data);
};

export const deleteInquiry = async (id) => {
  if (!isSupabaseConfigured) {
    return withLocalFallback(() => dbDeleteInquiry(id));
  }

  const { error } = await supabase.from(INQUIRIES_TABLE).delete().eq("id", id);

  if (error) {
    console.error("Error deleting inquiry from Supabase:", error);
    throw error;
  }

  return true;
};

export const getInquiryById = async (id) => {
  if (!isSupabaseConfigured) {
    return withLocalFallback(() => dbGetInquiryById(id));
  }

  const { data, error } = await supabase
    .from(INQUIRIES_TABLE)
    .select("*")
    .eq("id", id)
    .single();

  if (error) {
    console.error("Error fetching inquiry by ID from Supabase:", error);
    return null;
  }

  return normalizeInquiry(data);
};

export const clearAllInquiries = async () => {
  await initDB();
  const { clearAllData } = await import("./db");
  await clearAllData();
};
